import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hourglass } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { joApi } from '@/services/processingJoApi';
import { fmtN } from './JoBits';

/** Job-order stages watched by each section dashboard. */
export const STAGE_SETS = {
  greige: ['pick'],
  processing: ['processing', 'jobber'],
  inhouse: ['pi', 'folding', 'segregation', 'damage', 'pressing', 'assortment'],
  flexi: ['flexi', 'flexi_jobber', 'flexi_box'],
  sfg: ['sfg'],
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Alert strip: open job orders with pieces waiting more than 3 days at this section's stages. */
export const JoStuckBanner = ({ stages, testId = 'jo-stuck-banner', className = '' }) => {
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [open, setOpen] = useState(false);
  const key = stages.join(',');
  useEffect(() => { joApi.stuck(key.split(',')).then(setA).catch(() => {}); }, [key]);
  if (!a || !a.order_count) return null;
  return (
    <div className={`rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 ${className}`} data-testid={testId}>
      <div className="flex flex-wrap items-center gap-3">
        <Hourglass className="w-5 h-5 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900" data-testid={`${testId}-text`}>
            {plural(a.order_count, 'job order has', 'job orders have')} {fmtN(a.pcs)} pcs waiting more than {a.stuck_days} days here</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {a.stages.map((s) => (
              <span key={s.key} className="rounded border border-amber-200 bg-white/80 px-2 py-0.5 text-xs text-amber-900" data-testid={`${testId}-stage-${s.key}`}>
                {s.label} · {fmtN(s.pcs)} pcs · oldest {s.oldest_days}d</span>))}
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100" data-testid={`${testId}-toggle`}>
          {open ? 'Hide orders' : 'Show orders'}</Button>
      </div>
      {open && (
        <ul className="mt-2 divide-y divide-amber-200/70" data-testid={`${testId}-list`}>
          {a.rows.map((r) => (
            <li key={`${r.jo_id}-${r.key}`}>
              <button type="button" onClick={() => navigate(`/sections/processing/job-orders/${r.jo_id}`)} data-testid={`${testId}-row-${r.jo_id}-${r.key}`}
                className="flex w-full items-center gap-2 py-1.5 text-left text-xs text-amber-900 transition-colors hover:text-amber-700">
                <span className="font-mono font-semibold">{r.jo_id}</span>
                <span className="truncate">{r.fg_name}{r.design_name ? ` · ${r.design_name}` : ''}</span>
                <span className="ml-auto whitespace-nowrap font-medium">{r.label} · {fmtN(r.pcs)} pcs · {r.days}d</span>
              </button>
            </li>))}
          {a.rows.length < a.order_count && <li className="py-1.5 text-[11px] text-amber-800/80">Showing the 10 oldest</li>}
        </ul>)}
    </div>
  );
};

export default JoStuckBanner;
